import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  QueryList,
  ViewChild,
} from '@angular/core';

import { fromEvent as observableFromEvent, merge, Observable, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, map, switchMap, takeUntil } from 'rxjs/operators';

import { WindowRef } from '@recruitee/user-interface';

import { InsertPlaceholderInputDirective } from '../insert-placeholder-input.directive';
import { Placeholder, PlaceholderConfig, PlaceholderConfigMap } from '../placeholder';

type PlaceholderVisibility = {
  min: number;
  max: number;
  visiblePlaceholders: number;
};

@Component({
  selector: 'rt-placeholders-list',
  templateUrl: './placeholders-list.component.html',
  styleUrls: ['./placeholders-list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlaceholdersListComponent {
  @Input() public placeholders: Placeholder[];
  @Input() public isButtonVariant: boolean = false;

  @Input() public set target(target: InsertPlaceholderInputDirective) {
    this.targetsClear$.next();

    if (target instanceof InsertPlaceholderInputDirective) {
      this.targets$.next([target]);
    }
  }

  @Input() public set targets(targets: QueryList<InsertPlaceholderInputDirective>) {
    this.targetsClear$.next(); // always destroy existing subscriptions

    if (targets instanceof QueryList) {
      this.targets$.next(targets.toArray());

      targets.changes
        .pipe(
          takeUntil(merge(this.ngOnDestroy$.asObservable(), this.targetsClear$.asObservable())),
          map(_ => targets.toArray()),
        )
        .subscribe(this.targets$);
    } else {
      this.targets$.next([]);
    }
  }
  @ViewChild('rtPlaceholders') public placeholdersEl: any;

  public activeTarget: InsertPlaceholderInputDirective | undefined;
  public activeTargetConfig: PlaceholderConfigMap = {};
  public visiblePlaceholders: number = 1;
  public dropdownOpened: boolean = false;

  private ngOnDestroy$: Subject<void> = new Subject();
  private targetsClear$: Subject<void> = new Subject();
  private targets$: ReplaySubject<InsertPlaceholderInputDirective[]> = new ReplaySubject(1);
  private activeTarget$: Observable<InsertPlaceholderInputDirective | undefined> =
    this.createActiveTargetStream();

  private readonly placeholdersVisibilityByBreakpoint: PlaceholderVisibility[] = [
    { min: 0, max: 400, visiblePlaceholders: 1 },
    { min: 401, max: 500, visiblePlaceholders: 2 },
    { min: 501, max: 600, visiblePlaceholders: 3 },
    { min: 601, max: 650, visiblePlaceholders: 4 },
    { min: 651, max: 700, visiblePlaceholders: 5 },
    { min: 701, max: Number.POSITIVE_INFINITY, visiblePlaceholders: 6 },
  ];
  constructor(
    private cdRef: ChangeDetectorRef,
    private elRef: ElementRef,
    private window: WindowRef,
  ) {}

  public ngOnInit(): void {
    this.activeTarget$.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(target => {
      this.activeTarget = target;
      this.activeTargetConfig = target?.placeholdersConfig || {};
      this.cdRef.detectChanges();
    });
    this.checkVisibility();
    setTimeout(() => this.recalculateBreakpoint(), 100);
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public getConfig(id: string): PlaceholderConfig {
    return this.activeTargetConfig[id] || {};
  }

  public trackByName(index: number, placeholder: Placeholder): string {
    return placeholder.id;
  }

  public toggleDropdown(state: boolean = true): void {
    this.dropdownOpened = state;
    this.cdRef.detectChanges();
  }

  public withoutBrackets(str: string): string {
    return str.slice(1, -1);
  }

  private createActiveTargetStream(): Observable<InsertPlaceholderInputDirective | undefined> {
    return this.targets$.pipe(
      takeUntil(this.ngOnDestroy$.asObservable()),
      switchMap(targets =>
        merge(
          ...targets.map(target =>
            merge(
              target.input.focus.pipe(map(() => target)),
              target.input.blur.pipe(map(() => undefined)),
            ),
          ),
        ),
      ),
    );
  }

  private checkVisibility(): void {
    observableFromEvent(this.window.nativeWindow, 'resize')
      .pipe(debounceTime(50), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => {
        this.recalculateBreakpoint();
      });
  }

  private recalculateBreakpoint(): void {
    const containerWidth: number = this.elRef.nativeElement.offsetWidth;
    const currentBreakpoint = this.placeholdersVisibilityByBreakpoint.find(
      breakpoint => containerWidth >= breakpoint.min && containerWidth <= breakpoint.max,
    )!;
    this.visiblePlaceholders = currentBreakpoint.visiblePlaceholders;
    this.cdRef.detectChanges();
  }
}
